<template>
  <div class="c-tourplan-map">
    <div v-if="isOptimizingRoute" class="loading-wrapper is-loading loading-wrapper--map">
      <div class="loading-message"></div>
    </div>
    <div class="leaflet-map leaflet-map--map">
      <div :id="'rp-leaflet-map-' + _uid" style="width: 100%; height: 100%"></div>
      <div class="leaflet-map--map__message" v-if="message !== null">
        <span>{{ message }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import L from "leaflet";
import _ from "lodash";

export default {
  props: {
    zoom: {
      type: Number,
      default: 15
    },
    center: {
      type: Array,
      default: () => [48.140499, 11.53506]
    },
    removeAddresses: {
      uid: {
        type: [Number, String],
        default: ""
      },
      address: {
        type: String,
        default: ""
      },
      idx: {
        type: [Number, String],
        default: ""
      }
    },
    addresses: {
      id: {
        type: [Number, String],
        default: ""
      },
      address: {
        type: String,
        default: ""
      },
      idx: {
        type: [Number, String],
        default: ""
      }
    },
    startCity: {
      type: String,
      default: ""
    },
    startAddress: {
      type: String,
      default: ""
    },
    startZipcode: {
      type: String,
      default: ""
    },
    startLat: {
      type: String,
      default: ""
    },
    startLon: {
      type: String,
      default: ""
    },
    endCity: {
      type: String,
      default: ""
    },
    endAddress: {
      type: String,
      default: ""
    },
    endZipcode: {
      type: String,
      default: ""
    },
    endLat: {
      type: String,
      default: ""
    },
    endLon: {
      type: String,
      default: ""
    },
    resize: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      markers: [],
      leafletGroup: [],
      map: null,
      marker: null,
      message: null,
      sendRoute: [],
      optimizedRoute: [],
      routeAddresses: [],
      startGeo: {},
      startGeoReady: false,
      endGeoReady: false,
      endGeo: {},
      // The address string that `startGeo` / `endGeo` currently hold coordinates
      // for. While it matches the form fields we reuse those coordinates and
      // make no request at all; when the user edits the fields it stops
      // matching and we geocode again.
      startGeoKey: null,
      endGeoKey: null,
      isOptimizingRoute: false,
      routeLimit: process.env.VUE_APP_ROUTE_LIMIT,
      numOfTriesLimit: 2,
      numOfTries: 0,
      geocodeRetryLimit: 1
    };
  },
  watch: {
    resize: function(newVal, oldVal) {
      if (this.map) {
        this.map.invalidateSize();
      }
    },

    addresses: function(newVal, oldVal) {
      this.cleanMap();
      this.cleanRouteAddresses();
      this.addMapMarkers();
    }
  },
  mounted: function() {
    this.seedEndpointFromTour(
      "start",
      this.startAddress,
      this.startCity,
      this.startZipcode,
      this.startLat,
      this.startLon
    );
    this.seedEndpointFromTour(
      "end",
      this.endAddress,
      this.endCity,
      this.endZipcode,
      this.endLat,
      this.endLon
    );
    this.initializeMap();
    this.addMapMarkers();
  },

  methods: {
    initializeMap: function() {
      this.map = L.map("rp-leaflet-map-" + this._uid, {
        zoom: this.zoom,
        keyboard: false
      });

      this.map.setView(this.center);

      L.tileLayer(
        "https://cartodb-basemaps-{s}.global.ssl.fastly.net/rastertiles/voyager/{z}/{x}/{y}.png?key=" + process.env.VUE_APP_CARTO_KEY,
        {
          maxZoom: 18,
          id: "mapbox.light",
          attribution: ""
        }
      ).addTo(this.map);

      this.leafletGroup = new L.featureGroup();
    },

    addMapMarkers: function() {
      if (Object.keys(this.addresses).length === 0) {
        this.onUpdateNoAddress(this.$t("form.project.maps.no_valid_address"));
        return false;
      }

      this.message = null;

      let markerPromises = this.addresses.map(
        function(item, index) {
          return this.parseAddress(item.address, item)
            .then(address => {
              this.createMapMarker(
                address.lat,
                address.lon,
                item.name,
                item.uid,
                this.markerNumberIcon(index + 1)
              );
              this.addToRouteAddresses(address.lat, address.lon, item);
            })
            .catch(address => {
              this.handleInvalidAddress(address);
            });
        }.bind(this)
      );

      this.leafletGroup.addTo(this.map);

      // Fit the map only once every marker (stops + start/end) has resolved,
      // instead of guessing with a fixed timeout.
      return Promise.all([
        Promise.all(markerPromises),
        this.addRouteStartPoint().catch(() => {}),
        this.addRouteEndPoint().catch(() => {})
      ]).then(() => {
        this.setMap();
      });
    },

    getNameForUid: function(uid) {
      let key = this.addresses.findIndex(row => row.uid == uid);
      if (key === undefined || this.addresses[key] === undefined) {
        return;
      }

      let item = this.addresses[key];
      if (item.name) {
        return item.name;
      }
    },

    setMap: function() {
      let bounds = this.leafletGroup.getBounds();

      if (bounds.isValid()) {
        this.map.fitBounds(bounds);
      } else {
        this.map.setView(this.center);
      }
    },

    markerNumberIcon: function(number, markerClass, width, height) {
      return L.divIcon({
        className: "leaflet-marker-number " + markerClass,
        iconSize: [width ? width : 25, height ? height : 41],
        iconAnchor: [10, 44],
        popupAnchor: [3, -40],
        html: number ? number : ""
      });
    },

    createMapMarker: function(latitude, longitude, name, uid, markerIcon) {
      let marker = new L.Marker([latitude, longitude], {
        uid: uid,
        icon: markerIcon
      }).addTo(this.leafletGroup);
      marker.bindPopup(name);
    },

    addToRouteAddresses: function(latitude, longitude, address) {
      if (
        this.routeAddresses.find(row => row.uid == address.uid) === undefined
      ) {
        this.routeAddresses.push({
          uid: address.uid,
          name: address.name,
          address: address.address,
          lat: latitude,
          lng: longitude
        });
      }
      this.$emit("onAddressUpdate", address, latitude, longitude);
    },

    onUpdateNoAddress: function(message) {
      this.message = message;
    },

    removeMarkers: function() {
      this.removeAddresses.forEach(address => {
        let _vm = this;
        _vm.leafletGroup.eachLayer(function(layer) {
          if (layer.options.uid) {
            if (address.uid === layer.options.uid) {
              _vm.leafletGroup.removeLayer(layer);
            }
          }
        });
      });
      this.setMap();
    },

    removeRouteAddresses: function() {
      this.routeAddresses = [];
    },

    prepareRoute: function() {
      // Guard against the user triggering another run (e.g. double-clicking the
      // "optimize" button) while one is still in flight. Each run fires several
      // geocoding + RouteXL requests, and overlapping runs were the main cause
      // of the intermittent "startpoint could not be found" error.
      if (this.isOptimizingRoute) {
        window.app.$helpers.logger(
          "Route optimization already running, ignoring request."
        );
        return;
      }

      this.setOptimizing(true);

      Promise.all([this.addRouteStartPoint(), this.addRouteEndPoint()])
        .then(() => {
          this.sendRoute = [];
          this.optimizedRoute = [];

          this.routeAddresses.forEach(
            function(address) {
              this.sendRoute.push({
                lat: address.lat,
                lng: address.lng,
                address: address.uid,
                name: address.name
              });
            }.bind(this)
          );

          // Add start- and endpoints
          this.sendRoute.unshift(this.startGeo);
          this.sendRoute.push(this.endGeo);

          if (this.sendRoute.length < this.routeLimit) {
            this.callRouteApi();
          } else {
            this.setOptimizing(false);
            this.$emit(
              "onEndPointError",
              this.$t("tours.message.routelimitreached") +
                ": " +
                this.routeLimit
            );
          }
        })
        .catch(error => {
          this.setOptimizing(false);
          this.$emit("onEndPointError", error);
        });
    },

    setOptimizing: function(state) {
      this.isOptimizingRoute = state;
      this.$emit("onOptimizingStateChange", state);
    },

    addRouteStartPoint: function() {
      return new Promise((resolve, reject) => {
        if (!this.startZipcode) {
          window.app.$helpers.logger(this.$t("tours.map.startpoint.error"));
          reject(this.$t("tours.map.startpoint.error"));
          return;
        }

        if (this.startAddress === null) this.startAddress = "";
        if (this.startCity === null) this.startCity = "";

        let name = this.buildEndpointName(
          this.startAddress,
          this.startCity,
          this.startZipcode
        );

        this.resolveEndpoint(name, "start")
          .then(geo => {
            this.startGeo = geo;
            this.startGeoKey = name;
            this.createMapMarker(
              geo.lat,
              geo.lng,
              this.startZipcode,
              "start",
              this.markerNumberIcon("S", "leaflet-marker-number-red", 30, 49)
            );
            resolve();
          })
          .catch(error => {
            reject("startpoint error: " + error);
          });
      });
    },

    addRouteEndPoint: function() {
      return new Promise((resolve, reject) => {
        if (!this.endZipcode) {
          window.app.$helpers.logger(this.$t("tours.map.endpoint.error"));
          reject(this.$t("tours.map.endpoint.error"));
          return;
        }

        if (this.endAddress === null) this.endAddress = "";
        if (this.endCity === null) this.endCity = "";

        let name = this.buildEndpointName(
          this.endAddress,
          this.endCity,
          this.endZipcode
        );

        this.resolveEndpoint(name, "end")
          .then(geo => {
            this.endGeo = geo;
            this.endGeoKey = name;
            this.createMapMarker(
              geo.lat,
              geo.lng,
              this.endZipcode,
              "end",
              this.markerNumberIcon("E", "leaflet-marker-number-red", 30, 49)
            );
            resolve();
          })
          .catch(error => {
            reject("endpoint error: " + error);
          });
      });
    },

    buildEndpointName: function(address, city, zipcode) {
      return [address, city, zipcode]
        .map(part => (part || "").trim())
        .filter(part => part !== "")
        .join(" ");
    },

    /**
     * Seed `startGeo` / `endGeo` from the coordinates already stored on the tour
     * so an unchanged tour is optimized without any geocoding request. The
     * coordinates are tied to the address string they belong to (`*GeoKey`); as
     * soon as the user edits the zip/city/address fields that key stops matching
     * and `resolveEndpoint` geocodes the new address.
     */
    seedEndpointFromTour: function(type, address, city, zipcode, lat, lon) {
      if (!zipcode) {
        return;
      }
      let nlat = parseFloat(lat);
      let nlng = parseFloat(lon);
      if (isNaN(nlat) || isNaN(nlng) || nlat === 0 || nlng === 0) {
        return;
      }
      let name = this.buildEndpointName(address, city, zipcode);
      let geo = { lat: nlat, lng: nlng, address: type, name: name };
      if (type === "start") {
        this.startGeo = geo;
        this.startGeoKey = name;
      } else {
        this.endGeo = geo;
        this.endGeoKey = name;
      }
      window.app.$helpers.logger(
        "[routeplanner] " + type + "point: seeded from tour coords for '" + name + "'"
      );
    },

    /**
     * Resolve a start-/endpoint to `{ lat, lng, address, name }`.
     *
     * Reuses the coordinates already held for this exact address (from the tour
     * seed or an earlier resolution) and makes no request. Only when the address
     * string has changed does it call the backend `/geocode` endpoint, which
     * itself caches results in the `geocode_cache` table.
     */
    resolveEndpoint: function(name, type) {
      let currentKey = type === "start" ? this.startGeoKey : this.endGeoKey;
      let currentGeo = type === "start" ? this.startGeo : this.endGeo;

      if (currentKey === name && currentGeo && currentGeo.lat) {
        window.app.$helpers.logger(
          "[routeplanner] " + type + "point: reusing known coords for '" + name + "', no request"
        );
        return Promise.resolve(currentGeo);
      }

      window.app.$helpers.logger(
        "[routeplanner] " + type + "point: address is new/changed, calling backend /geocode for '" + name + "'"
      );
      return this.parseAddress(name).then(point => ({
        lat: point.lat,
        lng: point.lon,
        address: type,
        name: name
      }));
    },

    parseAddress: function(search, item) {
      return new Promise((resolve, reject) => {
        if (item && item.address && item.address.lat && item.address.lon) {
          resolve({ lat: item.address.lat, lon: item.address.lon });
          return;
        }

        if (item && item.project && item.project.lat && item.project.lon) {
          resolve({ lat: item.project.lat, lon: item.project.lon });
          return;
        }

        if (item) {
          window.app.$helpers.logger(
            "[routeplanner] stop '" +
              (item.name || item.uid) +
              "' has no stored coords, calling backend /geocode"
          );
        }

        let query = this.addressToString(search);

        this.geocodeViaApi(query, this.geocodeRetryLimit)
          .then(result => resolve({ lat: result.lat, lon: result.lng }))
          .catch(() => reject(item));
      });
    },

    /**
     * Normalized dedupe key for an address string. Mirrors the backend's
     * `GeocodingService::normalize()` (lowercase, collapse whitespace, trim) so
     * strings that resolve to the same cache row also share one in-flight
     * request here.
     */
    geocodeDedupeKey: function(query) {
      return String(query || "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
    },

    /**
     * Geocode through the backend `/geocode` endpoint (Google, with server-side
     * caching and a Nominatim fallback).
     *
     * One optimization run fires geocoding for every stop plus the start/end
     * points in parallel. When the same address appears more than once (e.g.
     * start == end, or a stop at the endpoint) those requests would all race
     * past the still-empty server-side cache and each hit Google. This collapses
     * concurrent lookups of the same address into a single in-flight request;
     * the entry is dropped once it settles, so a later run re-checks the
     * (now populated) server cache.
     */
    geocodeViaApi: function(query, retriesLeft) {
      let key = this.geocodeDedupeKey(query);

      if (!this._geocodeInflight) {
        this._geocodeInflight = {};
      }

      if (this._geocodeInflight[key]) {
        window.app.$helpers.logger(
          "[routeplanner] /geocode '" + query + "' deduped, reusing in-flight request"
        );
        return this._geocodeInflight[key];
      }

      let request = this.requestGeocode(query, retriesLeft);
      this._geocodeInflight[key] = request;

      let evict = () => {
        if (this._geocodeInflight[key] === request) {
          delete this._geocodeInflight[key];
        }
      };
      request.then(evict, evict);

      return request;
    },

    /**
     * The actual `POST /geocode` call. Retries a limited number of times with a
     * short delay to ride out transient failures. Not deduplicated -
     * `geocodeViaApi` is the entry point that collapses concurrent lookups.
     */
    requestGeocode: function(query, retriesLeft) {
      window.app.$helpers.logger(
        "[routeplanner] POST /geocode -> '" + query + "' (retries left: " + retriesLeft + ")"
      );
      return this.$geocoder.geocodeAddress(query).catch(error => {
        let status =
          error && error.response ? error.response.status : null;
        // Only retry transient failures - a 4xx (e.g. address not found) will
        // not get better by asking again.
        let isTransient = status === null || status >= 500;

        if (retriesLeft > 0 && isTransient) {
          return new Promise(resolve => setTimeout(resolve, 1200)).then(() =>
            this.requestGeocode(query, retriesLeft - 1)
          );
        }
        return Promise.reject(error);
      });
    },

    addressToString: function(address) {
      if (typeof address === "string") {
        return address;
      }

      let output = "";
      if (typeof address === "object" || Array.isArray(address)) {
        if (address.street) {
          output += address.street.trim();
        }
        if (address.housenumber) {
          output += " " + address.housenumber.trim();
        }
        if (address.zipcode) {
          output += " " + address.zipcode.trim();
        }
        if (address.city) {
          output += " " + address.city.trim();
        }
      }

      return output;
    },

    handleInvalidAddress: function(address) {
      this.$emit("onAddressError", address);
    },

    onCalculateRouteError: function() {
      this.$emit(
        "onCalculateRouteError",
        this.$t("tours.error.routeoptserver")
      );
    },

    cleanRouteAddresses: function() {
      this.routeAddresses = [];
    },

    cleanMap: function() {
      this.leafletGroup.eachLayer(
        function(layer) {
          this.leafletGroup.removeLayer(layer);
        }.bind(this)
      );
    },

    retryRouteAPICall: _.debounce(function() {
      this.callRouteApi();
      this.numOfTries++;
    }, 2000),

    callRouteApi: function() {
      window.app.$helpers.logger("Calling RouteXL ...");
      this.setOptimizing(true);

      let postData = { locations: JSON.stringify(this.sendRoute) };

      let promise = this.$api.clearParams().post("routexl", postData);

      promise
        .then(response => {
          this.optimizedRoute = response.data.data.route;
          if (this.optimizedRoute.length > 0) {
            let optimizedRouteUids = [];
            this.cleanMap();
            let markerIdx = 1;
            let distance = 0;
            this.optimizedRoute.forEach(
              function(address, index) {
                this.sendRoute.find(
                  function(item) {
                    if (item.address == address.name) {
                      let addressName = this.getNameForUid(address.name);
                      let number = "";
                      let markerClass = "";
                      let markerWidth = 25;
                      let markerHeight = 41;
                      if (
                        index === 0 ||
                        index === this.optimizedRoute.length - 1
                      ) {
                        number = index == 0 ? "S" : "E";
                        markerClass = "leaflet-marker-number-red";
                        markerWidth = 30;
                        markerHeight = 49;
                      } else {
                        number = markerIdx.toString();
                        markerIdx++;
                      }
                      this.createMapMarker(
                        item.lat,
                        item.lng,
                        addressName ? addressName : item.name,
                        number,
                        this.markerNumberIcon(
                          number,
                          markerClass,
                          markerWidth,
                          markerHeight
                        )
                      );

                      // Calculating distance between each point as the api gives us a cummulative distance
                      let calculatedDistance = address.distance - distance;
                      distance = address.distance;

                      optimizedRouteUids.push({
                        address: item.address,
                        distance: calculatedDistance.toFixed(2)
                      });
                    }
                  }.bind(this)
                );
              }.bind(this)
            );
            this.$emit("onCalculateRoute", optimizedRouteUids);
            this.$emit("onEndpointUpdate", {
              start_lat: this.startGeo.lat,
              start_lng: this.startGeo.lng,
              end_lat: this.endGeo.lat,
              end_lng: this.endGeo.lng
            });
            this.setMap();
            this.setOptimizing(false);
            this.numOfTries = 0;
          } else {
            // RouteXL answered but returned an empty route.
            this.onCalculateRouteError();
            this.setOptimizing(false);
            this.numOfTries = 0;
          }
        })
        .catch(error => {
          if (this.numOfTriesLimit <= this.numOfTries) {
            this.onCalculateRouteError();
            window.app.$helpers.logger(error);
            this.setOptimizing(false);
            this.numOfTries = 0;
          } else {
            this.retryRouteAPICall();
            window.app.$helpers.logger("Retrying RouteXL ...");
          }
        });
      return promise;
    }
  }
};
</script>